import type { Editor } from "@tiptap/react";
import { useEffect, useRef } from "react";
import type { RefObject } from "react";

/**
 * Syncs TipTap editor content with a content prop.
 * Uses `setContent` / `clearContent` with `emitUpdate: false` so programmatic sync does not fire TipTap `update` (avoids redundant parent `onUpdate` / Zustand writes).
 * The updating ref is cleared in a `finally` block in the same turn—no `setTimeout`, since suppressed updates do not require a deferred reset.
 * @param editor - The TipTap editor instance
 * @param content - The content prop to sync with
 * @param isUpdatingRef - Optional ref to track update state (if provided, will be used instead of creating a new one)
 * @returns The ref used to track update state for use in `onUpdate` callbacks
 */
export const useEditorContentSync = (
  editor: Editor | null,
  content: string | undefined,
  isUpdatingRef?: RefObject<boolean>,
) => {
  const internalRef = useRef(false);
  const ref = isUpdatingRef ?? internalRef;

  useEffect(() => {
    if (!editor || content === undefined) return;

    const currentContent = editor.getText().trim();
    const newContent = (content || "").trim();

    if (currentContent !== newContent && !ref.current) {
      ref.current = true;
      try {
        if (newContent === "") {
          editor.commands.clearContent(false);
        } else {
          editor.commands.setContent(newContent, { emitUpdate: false });
        }
      } finally {
        ref.current = false;
      }
    }
  }, [editor, content, ref]);

  return ref;
};
